/**
 * SpotlightRenderer — Dim overlay with ERASE cutout(s), pulsing highlight borders,
 * optional label + directional arrow, and 2-layer input restriction.
 *
 * Supports multiple highlighted rects (multi-highlight) for tutorial-style
 * highlighting of many tiles/buttons at once.
 *
 * Drawing technique reused from TutorialOverlay (ERASE blend mode cutout).
 * Decoupled from TutorialStep interface for use in the cinematic system.
 *
 * For world-space targets, re-projects rects to screen coords every frame
 * to track camera movement (pan, zoom, dynamic camera).
 */
import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { TECH } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';
import { UITargetRegistry, type ScreenRect } from './UITargetRegistry';
import { t } from '@/i18n/I18n';

const DEPTH_DIM = 490;
const DEPTH_HIGHLIGHT = 491;
const DEPTH_BLOCKER = 492;
const DEPTH_CLICKABLE = 493;
const DEPTH_LABEL = 494;
const CUTOUT_PAD = 6;
const CUTOUT_RADIUS = 6;
const ARROW_HW = 8;
const ARROW_DEPTH = 12;

export interface SpotlightOptions {
  dimAlpha?: number;
  highlightColor?: number;
  pulse?: boolean;
  labelKey?: string;
  labelPosition?: 'top' | 'bottom' | 'left' | 'right';
  arrowDir?: 'up' | 'down' | 'left' | 'right';
  restrictInput?: boolean;
}

export class SpotlightRenderer {
  private scene: Phaser.Scene;

  private dimGfx: Phaser.GameObjects.Graphics | null = null;
  private highlightGfx: Phaser.GameObjects.Graphics | null = null;
  private blockerRect: Phaser.GameObjects.Rectangle | null = null;
  private clickableRects: Phaser.GameObjects.Rectangle[] = [];
  private labelText: Phaser.GameObjects.Text | null = null;
  private arrowGfx: Phaser.GameObjects.Graphics | null = null;
  private pulseTween: Phaser.Tweens.Tween | null = null;

  // ── Per-frame camera tracking state ──
  private worldRects: ScreenRect[] | null = null;
  private hostScene: Phaser.Scene | null = null;
  private storedOpts: SpotlightOptions = {};
  private lastCamX = NaN;
  private lastCamY = NaN;
  private lastCamZ = NaN;
  private updateHandler: (() => void) | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Public API ──

  /**
   * Resolve a step target to world-space ScreenRect[].
   * Handles: string (named single/multi), ScreenRect, ScreenRect[].
   * Returns world rects (no projection) — caller decides when to project.
   */
  static resolveWorldRects(
    target: string | ScreenRect | ScreenRect[],
    screenSpace: boolean | undefined,
    hostScene: Phaser.Scene,
  ): { rects: ScreenRect[]; isScreen: boolean } | null {
    if (Array.isArray(target)) {
      return target.length > 0 ? { rects: target, isScreen: !!screenSpace } : null;
    }
    if (typeof target === 'object') {
      return { rects: [target], isScreen: !!screenSpace };
    }
    // Named target
    const multiBounds = UITargetRegistry.getMultiBounds(target);
    if (multiBounds && multiBounds.length > 0) {
      return { rects: multiBounds, isScreen: !!screenSpace };
    }
    const bounds = UITargetRegistry.getBounds(target);
    if (!bounds) {
      console.warn(`[SpotlightRenderer] Unknown target: "${target}"`);
      return null;
    }
    return { rects: [bounds], isScreen: !!screenSpace };
  }

  /**
   * Resolve a step target to ScreenRect[] (legacy — projects immediately).
   * Kept for backwards compat with EditorLivePreview.
   */
  static resolveTarget(
    target: string | ScreenRect | ScreenRect[],
    screenSpace: boolean | undefined,
    hostScene: Phaser.Scene,
  ): ScreenRect[] | null {
    const resolved = SpotlightRenderer.resolveWorldRects(target, screenSpace, hostScene);
    if (!resolved) return null;
    if (resolved.isScreen) return resolved.rects;
    return resolved.rects.map(b => projectToScreen(b, hostScene));
  }

  /**
   * Show spotlight with per-frame camera tracking.
   * For world-space rects, re-projects every frame to follow camera movement.
   */
  showWorld(
    worldRects: ScreenRect[],
    hostScene: Phaser.Scene,
    isScreenSpace: boolean,
    opts: SpotlightOptions = {},
  ): void {
    this.hide();

    this.storedOpts = opts;

    if (isScreenSpace) {
      // Screen-space targets don't need per-frame tracking
      this.worldRects = null;
      this.hostScene = null;
      this.draw(worldRects, opts);
      return;
    }

    // Store world rects for per-frame re-projection
    this.worldRects = worldRects;
    this.hostScene = hostScene;

    // Initial draw
    const screenRects = worldRects.map(r => projectToScreen(r, hostScene));
    this.draw(screenRects, opts);
    this.saveCamState(hostScene);

    // Register per-frame update
    this.updateHandler = () => this.onUpdate();
    this.scene.events.on('update', this.updateHandler);
  }

  /** Show spotlight with pre-projected screen rects (no camera tracking). Legacy API. */
  show(rects: ScreenRect[], opts: SpotlightOptions = {}): void {
    this.hide();
    this.worldRects = null;
    this.hostScene = null;
    this.storedOpts = {};
    this.draw(rects, opts);
  }

  /** Remove all spotlight visuals and input zones */
  hide(): void {
    // Remove update listener
    if (this.updateHandler) {
      this.scene.events.off('update', this.updateHandler);
      this.updateHandler = null;
    }
    this.worldRects = null;
    this.hostScene = null;

    this.destroyVisuals();
  }

  destroy(): void {
    this.hide();
  }

  // ── Per-frame update ──

  private onUpdate(): void {
    if (!this.worldRects || !this.hostScene) return;

    const cam = this.hostScene.cameras?.main;
    if (!cam) return;

    // Skip redraw if camera hasn't moved
    if (cam.scrollX === this.lastCamX &&
        cam.scrollY === this.lastCamY &&
        cam.zoom === this.lastCamZ) {
      return;
    }

    // Camera changed — re-project and redraw
    const screenRects = this.worldRects.map(r => projectToScreen(r, this.hostScene!));
    this.destroyVisuals();
    this.draw(screenRects, this.storedOpts);
    this.saveCamState(this.hostScene);
  }

  private saveCamState(hostScene: Phaser.Scene): void {
    const cam = hostScene.cameras?.main;
    if (!cam) return;
    this.lastCamX = cam.scrollX;
    this.lastCamY = cam.scrollY;
    this.lastCamZ = cam.zoom;
  }

  // ── Drawing (creates all visual game objects) ──

  private draw(rects: ScreenRect[], opts: SpotlightOptions): void {
    const dimAlpha = opts.dimAlpha ?? 0.55;
    const highlightColor = opts.highlightColor ?? TECH.ACCENT;
    const pulse = opts.pulse ?? true;
    const restrictInput = opts.restrictInput ?? true;

    // ── Dim overlay with ERASE cutouts ──
    this.dimGfx = this.scene.add.graphics().setDepth(DEPTH_DIM);
    this.dimGfx.fillStyle(0x000000, dimAlpha);
    this.dimGfx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    this.dimGfx.fillStyle(0x000000, 0);
    this.dimGfx.setBlendMode(Phaser.BlendModes.ERASE);
    for (const r of rects) {
      this.dimGfx.fillRoundedRect(
        r.x - CUTOUT_PAD, r.y - CUTOUT_PAD,
        r.w + CUTOUT_PAD * 2, r.h + CUTOUT_PAD * 2,
        CUTOUT_RADIUS,
      );
    }
    this.dimGfx.setBlendMode(Phaser.BlendModes.NORMAL);

    // ── Highlight borders ──
    this.highlightGfx = this.scene.add.graphics().setDepth(DEPTH_HIGHLIGHT);
    this.highlightGfx.lineStyle(2.5, highlightColor, 0.9);
    for (const r of rects) {
      this.highlightGfx.strokeRoundedRect(
        r.x - CUTOUT_PAD, r.y - CUTOUT_PAD,
        r.w + CUTOUT_PAD * 2, r.h + CUTOUT_PAD * 2,
        CUTOUT_RADIUS,
      );
    }

    if (pulse) {
      this.pulseTween = this.scene.tweens.add({
        targets: this.highlightGfx,
        alpha: { from: 1, to: 0.3 },
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // ── Input restriction (2-layer pattern) ──
    if (restrictInput) {
      this.blockerRect = this.scene.add.rectangle(
        CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_WIDTH, CANVAS_HEIGHT,
        0x000000, 0,
      ).setDepth(DEPTH_BLOCKER).setInteractive();
      this.blockerRect.on('pointerdown', () => { /* blocked */ });
    }

    // Clickable zones — one per highlighted rect
    for (const r of rects) {
      const zone = this.scene.add.rectangle(
        r.x + r.w / 2, r.y + r.h / 2,
        r.w + CUTOUT_PAD * 2, r.h + CUTOUT_PAD * 2,
        0x000000, 0,
      ).setDepth(DEPTH_CLICKABLE).setInteractive({ useHandCursor: true });
      zone.on('pointerdown', () => {
        EventBus.emit(CinematicEvents.SPOTLIGHT_CLICKED);
      });
      this.clickableRects.push(zone);
    }

    // ── Label (positioned relative to centroid of all rects) ──
    const centroid = computeCentroid(rects);
    const bbox = computeBoundingBox(rects);

    if (opts.labelKey) {
      const labelPos = opts.labelPosition ?? 'bottom';
      const labelStr = t(opts.labelKey);
      this.labelText = this.scene.add.text(0, 0, labelStr, {
        fontSize: '13px', color: '#ffcc00', fontFamily: 'monospace',
        fontStyle: 'bold', align: 'center',
      }).setDepth(DEPTH_LABEL);

      switch (labelPos) {
        case 'top':
          this.labelText.setOrigin(0.5, 1).setPosition(centroid.x, bbox.y - CUTOUT_PAD - 6);
          break;
        case 'bottom':
          this.labelText.setOrigin(0.5, 0).setPosition(centroid.x, bbox.y + bbox.h + CUTOUT_PAD + 6);
          break;
        case 'left':
          this.labelText.setOrigin(1, 0.5).setPosition(bbox.x - CUTOUT_PAD - 8, centroid.y);
          break;
        case 'right':
          this.labelText.setOrigin(0, 0.5).setPosition(bbox.x + bbox.w + CUTOUT_PAD + 8, centroid.y);
          break;
      }
    }

    // ── Arrow (directional triangle from label edge toward highlight centroid) ──
    if (opts.arrowDir && this.labelText) {
      this.drawArrow(rects, this.labelText, opts.arrowDir, highlightColor);
    }
  }

  /** Destroy all visual game objects (but don't clear tracking state) */
  private destroyVisuals(): void {
    if (this.pulseTween) { this.pulseTween.destroy(); this.pulseTween = null; }
    if (this.dimGfx) { this.dimGfx.destroy(); this.dimGfx = null; }
    if (this.highlightGfx) { this.highlightGfx.destroy(); this.highlightGfx = null; }
    if (this.blockerRect) { this.blockerRect.destroy(); this.blockerRect = null; }
    for (const z of this.clickableRects) z.destroy();
    this.clickableRects = [];
    if (this.labelText) { this.labelText.destroy(); this.labelText = null; }
    if (this.arrowGfx) { this.arrowGfx.destroy(); this.arrowGfx = null; }
  }

  // ── Private ──

  /**
   * Draw a directional arrow triangle from the label edge toward the highlight centroid.
   * Geometry matches TutorialOverlay.drawArrow() (TECH.ACCENT filled triangle).
   */
  private drawArrow(
    rects: ScreenRect[],
    label: Phaser.GameObjects.Text,
    arrowDir: 'up' | 'down' | 'left' | 'right',
    color: number,
  ): void {
    const centroid = computeCentroid(rects);
    const lx = label.x - label.originX * label.width;
    const ly = label.y - label.originY * label.height;
    const lw = label.width;
    const lh = label.height;

    this.arrowGfx = this.scene.add.graphics().setDepth(DEPTH_LABEL);
    this.arrowGfx.fillStyle(color, 0.8);

    let ax: number, ay: number;
    switch (arrowDir) {
      case 'down':
        ax = Phaser.Math.Clamp(centroid.x, lx + 20, lx + lw - 20);
        ay = ly + lh;
        this.arrowGfx.fillTriangle(ax - ARROW_HW, ay, ax + ARROW_HW, ay, ax, ay + ARROW_DEPTH);
        break;
      case 'up':
        ax = Phaser.Math.Clamp(centroid.x, lx + 20, lx + lw - 20);
        ay = ly;
        this.arrowGfx.fillTriangle(ax - ARROW_HW, ay, ax + ARROW_HW, ay, ax, ay - ARROW_DEPTH);
        break;
      case 'left':
        ax = lx;
        ay = Phaser.Math.Clamp(centroid.y, ly + 10, ly + lh - 10);
        this.arrowGfx.fillTriangle(ax, ay - ARROW_HW, ax, ay + ARROW_HW, ax - ARROW_DEPTH, ay);
        break;
      case 'right':
        ax = lx + lw;
        ay = Phaser.Math.Clamp(centroid.y, ly + 10, ly + lh - 10);
        this.arrowGfx.fillTriangle(ax, ay - ARROW_HW, ax, ay + ARROW_HW, ax + ARROW_DEPTH, ay);
        break;
    }
  }
}

// ── Helpers ──

function projectToScreen(bounds: ScreenRect, hostScene: Phaser.Scene): ScreenRect {
  const cam = hostScene.cameras?.main;
  if (!cam) return bounds;
  const halfW = cam.width / 2;
  const halfH = cam.height / 2;
  return {
    x: (bounds.x - cam.scrollX - halfW) * cam.zoom + halfW,
    y: (bounds.y - cam.scrollY - halfH) * cam.zoom + halfH,
    w: bounds.w * cam.zoom,
    h: bounds.h * cam.zoom,
  };
}

function computeCentroid(rects: ScreenRect[]): { x: number; y: number } {
  let tx = 0, ty = 0;
  for (const r of rects) { tx += r.x + r.w / 2; ty += r.y + r.h / 2; }
  return { x: tx / rects.length, y: ty / rects.length };
}

function computeBoundingBox(rects: ScreenRect[]): ScreenRect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w);
    maxY = Math.max(maxY, r.y + r.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
